import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Check, ArrowRight, Moon, Leaf, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IMAGES } from "@/lib/constants";
import { BookingFlow } from "@/components/BookingFlow";
import { useGuestyListings } from "@/components/LiveAvailability";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const amenities = [
  "Queen Bed", "Luxury Bedding", "Ambient Lighting", "Outdoor Seating Area",
  "Shared Bathhouse", "Fire Pit Access", "Picnic Table", "Parking",
];

const highlights = [
  { icon: Moon, title: "Stargazing", desc: "Fall asleep under the Texas sky" },
  { icon: Leaf, title: "Nature Immersion", desc: "Surrounded by trees and wildlife" },
  { icon: Wind, title: "Fresh Air Living", desc: "Open-air luxury experience" },
];

export default function GlampingPage() {
  const { listings } = useGuestyListings("tent");
  const maxGuests = listings.length ? Math.max(...listings.map((l) => l.accommodates || 0)) : 0;
  return (
    <>
      <Helmet>
        <title>Glamping Safari Tents | Luxury Glamping Near Lake Texoma - Texoma Resorts</title>
        <meta name="description" content="Experience luxury glamping in our safari-style tents near Lake Texoma. Queen beds, ambient lighting, campfire access, and nature all around at Serenity Village." />
      </Helmet>

      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.glamping1} alt="Glamping safari tents at Texoma Resorts" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Luxury Meets Outdoors</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              Glamping Safari Tents
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="caption-text text-accent mb-3">About Our Tents</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-6">
                The Perfect Blend of Adventure & Comfort
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                Our safari-style glamping tents bring the outdoors in with luxurious bedding,
                ambient lighting, and nature all around you. Experience the magic of camping
                without sacrificing comfort.
              </p>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-8">
                Each tent is thoughtfully designed to create a cozy retreat while keeping you
                connected to the beauty of the Texas outdoors near Lake Texoma.
              </p>
              <Badge data-testid="glamping-guests-badge" className="bg-primary text-white font-body text-xs mb-6">Up to {maxGuests || 4} Guests</Badge>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2">
                    <Check size={14} className="text-primary shrink-0" />
                    <span className="font-body text-sm text-foreground/80">{a}</span>
                  </div>
                ))}
              </div>
              <a href="#book">
                <Button data-testid="glamping-book-now" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
                  Check Availability <ArrowRight size={16} className="ml-2" />
                </Button>
              </a>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 img-hover-zoom rounded-lg overflow-hidden aspect-video">
                  <img src={IMAGES.glamping2} alt="Safari tent interior" className="w-full h-full object-cover" />
                </div>
                <div className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
                  <img src={IMAGES.gallery4} alt="Glamping area" className="w-full h-full object-cover" />
                </div>
                <div className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
                  <img src={IMAGES.gallery5} alt="Glamping surroundings" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="book" data-testid="glamping-booking-section" className="py-16 md:py-24 bg-white border-y">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="caption-text text-accent mb-3">Book Direct & Save</p>
            <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-2">
              Book a Safari Tent
            </h2>
            <p className="font-body text-sm text-muted-foreground mb-6">
              Choose your tent, select dates on the calendar, and pay securely — your
              reservation is confirmed instantly.
            </p>
            <BookingFlow listingFilter="tent" />
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {highlights.map((h, i) => (
              <motion.div key={h.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/5 flex items-center justify-center">
                    <h.icon size={24} className="text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{h.title}</h3>
                  <p className="font-body text-sm text-muted-foreground">{h.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
